import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';
import DarkModeWrapper from '../../../components/DarkModeWrapper';

const DownloadableResourcesSection = () => {
  const { isDark } = useTheme();
  const [downloadCounts, setDownloadCounts] = useState({
    cv: 1247,
    manifesto: 892,
    certifications: 634,
    portfolio: 1156,
    references: 423,
    timeline: 789
  });

  const resources = [
    {
      id: 'cv',
      title: "Professional CV",
      description: "Comprehensive resume with detailed work experience, technical skills, and project highlights.",
      icon: "FileText",
      color: "primary",
      fileSize: "2.4 MB",
      format: "PDF",
      lastUpdated: "January 2025",
      features: [
        "Detailed work experience timeline",
        "Technical skills matrix",
        "Project case studies",
        "Professional references"
      ]
    },
    {
      id: 'manifesto',
      title: "Development Manifesto",
      description: "My personal philosophy on modern web development, best practices, and industry insights.",
      icon: "BookOpen",
      color: "success",
      fileSize: "1.8 MB",
      format: "PDF",
      lastUpdated: "December 2024",
      features: [
        "Development philosophy",
        "Best practices guide",
        "Industry trend analysis",
        "Career advice for developers"
      ]
    },
    {
      id: 'certifications',
      title: "Technical Certifications",
      description: "Collection of professional certifications, course completions, and technical achievements.",
      icon: "Award",
      color: "brand-purple",
      fileSize: "3.2 MB",
      format: "PDF",
      lastUpdated: "January 2025",
      features: [
        "AWS certifications",
        "React specialization certificates",
        "University transcripts",
        "Workshop completion certificates"
      ]
    },
    {
      id: 'portfolio',
      title: "Project Portfolio",
      description: "Detailed case studies of major projects with technical specifications and outcomes.",
      icon: "Folder",
      color: "accent",
      fileSize: "5.1 MB",
      format: "PDF",
      lastUpdated: "January 2025",
      features: [
        "15+ project case studies",
        "Technical architecture diagrams",
        "Performance metrics",
        "Client testimonials"
      ]
    },
    {
      id: 'references',
      title: "Professional References",
      description: "Contact information and recommendations from colleagues, clients, and mentors.",
      icon: "Users",
      color: "warning",
      fileSize: "0.8 MB",
      format: "PDF",
      lastUpdated: "December 2024",
      features: [
        "Manager recommendations",
        "Client testimonials",
        "Peer endorsements",
        "Academic references"
      ]
    },
    {
      id: 'timeline',
      title: "Career Timeline",
      description: "Visual timeline of my professional journey with key milestones and achievements.",
      icon: "Clock",
      color: "error",
      fileSize: "1.5 MB",
      format: "PDF",
      lastUpdated: "January 2025",
      features: [
        "Interactive career timeline",
        "Key milestone highlights",
        "Skill development progression",
        "Achievement gallery"
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: {
        bg: "bg-primary",
        text: "text-primary",
        light: "bg-primary/10",
        border: "border-primary/20"
      },
      success: {
        bg: "bg-success",
        text: "text-success",
        light: "bg-success/10",
        border: "border-success/20"
      },
      "brand-purple": {
        bg: "bg-brand-purple",
        text: "text-brand-purple",
        light: "bg-brand-purple/10",
        border: "border-brand-purple/20"
      },
      accent: {
        bg: "bg-accent",
        text: "text-accent",
        light: "bg-accent/10",
        border: "border-accent/20"
      },
      warning: {
        bg: "bg-warning",
        text: "text-warning",
        light: "bg-warning/10",
        border: "border-warning/20"
      },
      error: {
        bg: "bg-error",
        text: "text-error",
        light: "bg-error/10",
        border: "border-error/20"
      }
    };
    return colorMap[color] || colorMap.primary;
  };

  const handleDownload = (resourceId) => {
    // Simulate download and increment counter
    setDownloadCounts(prev => ({
      ...prev,
      [resourceId]: prev[resourceId] + 1
    }));
    
    // In a real application, this would trigger the actual download
    console.log(`Downloading ${resourceId}...`);
  };

  return (
    <DarkModeWrapper sectionName="resources">
      <section className={`py-20 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="Download" size={16} />
              <span>Downloadable Resources</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Professional Documents & Resources
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Access comprehensive documentation of my professional journey, technical expertise, 
              and development philosophy through these carefully curated resources.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resources.map((resource) => {
              const colors = getColorClasses(resource.color);
              
              return (
                <div key={resource.id} className={`${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-2xl border shadow-premium hover:shadow-xl transition-premium`}>
                  <div className="p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-14 h-14 ${colors.bg} rounded-2xl flex items-center justify-center`}>
                        <Icon name={resource.icon} size={28} className="text-white" />
                      </div>
                      <div className="text-right">
                        <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-text-muted'}`}>{downloadCounts[resource.id]} downloads</div>
                        <div className={`flex items-center space-x-1 text-xs ${isDark ? 'text-slate-400' : 'text-text-muted'}`}>
                          <Icon name="Calendar" size={12} />
                          <span>{resource.lastUpdated}</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-text-primary mb-2">{resource.title}</h3>
                      <p className="text-text-secondary text-sm leading-relaxed mb-4">
                        {resource.description}
                      </p>

                      {/* File Info */}
                      <div className={`flex items-center space-x-4 text-xs ${isDark ? 'text-slate-400' : 'text-text-muted'} mb-4`}>
                        <div className="flex items-center space-x-1">
                          <Icon name="File" size={12} />
                          <span>{resource.format}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="HardDrive" size={12} />
                          <span>{resource.fileSize}</span>
                        </div>
                      </div>

                      {/* Features */}
                      <div className={`${colors.light} rounded-xl p-4 border ${isDark ? 'border-gray-700' : colors.border}`}>
                        <h4 className={`font-medium ${colors.text} mb-3 text-sm`}>What's Included:</h4>
                        <div className="space-y-2">
                          {resource.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-center space-x-2">
                              <Icon name="Check" size={12} className={colors.text} />
                              <span className="text-text-secondary text-xs">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Download Button */}
                    <Button
                      variant="default"
                      fullWidth
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => handleDownload(resource.id)}
                      className="hover-lift"
                    >
                      Download {resource.format}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Download Stats */}
          <div className={`mt-16 ${isDark ? 'bg-gradient-to-br from-slate-800 to-slate-700' : 'bg-gradient-to-br from-slate-50 to-blue-50'} rounded-2xl p-8`}>
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-text-primary mb-2">Download Statistics</h3>
              <p className="text-text-secondary">Resource popularity and engagement metrics</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-1">
                  {Object.values(downloadCounts).reduce((a, b) => a + b, 0).toLocaleString()}
                </div>
                <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-text-muted'}`}>Total Downloads</div>
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-success mb-1">6</div>
                <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-text-muted'}`}>Resource Types</div>
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-brand-purple mb-1">98%</div>
                <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-text-muted'}`}>Satisfaction Rate</div>
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-1">24/7</div>
                <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-text-muted'}`}>Availability</div>
              </div>
            </div>
          </div>

          {/* Contact for Custom Resources */}
          <div className="mt-16 bg-gradient-to-r from-primary to-brand-purple rounded-2xl p-8 text-white text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Icon name="MessageCircle" size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Need Custom Documentation?</h3>
              <p className="text-xl opacity-90 mb-6">
                Looking for specific project details, technical specifications, or custom reports? 
                I can provide tailored documentation to meet your requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="outline"
                  iconName="Mail"
                  iconPosition="left"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Request Custom Docs
                </Button>
                <Button
                  variant="outline"
                  iconName="Calendar"
                  iconPosition="left"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Schedule Discussion
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </DarkModeWrapper>
  );
};

export default DownloadableResourcesSection;